/**
 * File Tree Node Component
 * Recursive component for displaying file/folder trees with hide/show functionality
 */

import React, { useState, useEffect } from 'react';
import { File, Folder, ChevronDown, ChevronRight, Eye, EyeOff, CheckCircle } from 'lucide-react';

interface FileTreeNodeProps {
  node: any;
  level?: number;
  toggleHidePath: (path: string, name: string) => void;
  hiddenPaths: Set<string>;
  formatFileSize: (bytes: number) => string;
  supportedExtensions: Set<string>;
  extensionDescriptions: Record<string, string>;
  selectedExtensions: string[];
  expandedTreePaths: Set<string>;
  setExpandedTreePaths: React.Dispatch<React.SetStateAction<Set<string>>>;
}

export const FileTreeNode = React.memo<FileTreeNodeProps>(({ 
  node, 
  level = 0, 
  toggleHidePath, 
  hiddenPaths, 
  formatFileSize, 
  supportedExtensions, 
  extensionDescriptions, 
  selectedExtensions, 
  expandedTreePaths, 
  setExpandedTreePaths 
}) => {
  const nodePath = node.path || node.name;
  const isHidden = hiddenPaths.has(nodePath);
  
  const getExpandedState = () => {
    const isExpandedByPath = expandedTreePaths.has(nodePath);
    const isExpandedByName = expandedTreePaths.has(node.name);
    // Always start expanded by default
    const shouldAutoExpand = expandedTreePaths.size === 0;
    return isExpandedByPath || isExpandedByName || shouldAutoExpand;
  };
  
  const [expanded, setExpanded] = useState(true); // Start fully expanded
  
  useEffect(() => {
    const shouldBeExpanded = getExpandedState();
    setExpanded(shouldBeExpanded);
  }, [expandedTreePaths, nodePath, node.name, level]);
  
  const handleToggleExpand = () => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    setExpandedTreePaths(prev => {
      const newSet = new Set(prev);
      if (newExpanded) {
        newSet.add(nodePath);
      } else {
        newSet.delete(nodePath);
      }
      return newSet;
    });
  };
  
  if (node.type === 'file') {
    const ext = node.extension || '';
    const isSupported = supportedExtensions.has(ext);
    const description = extensionDescriptions[ext] || 'Unknown file type';
    const shouldShow = selectedExtensions.length === 0 || selectedExtensions.includes(ext);
    
    if (!shouldShow) return null;
    
    return (
      <div className="flex items-center gap-2 py-1 text-sm hover:bg-gray-50 group min-h-[28px]" style={{ paddingLeft: `${level * 20 + 4}px` }}>
        <File className={`h-4 w-4 flex-shrink-0 ${isSupported ? 'text-green-500' : 'text-gray-400'}`} />
        <span className={`text-gray-700 flex-1 ${isHidden ? 'opacity-50 line-through' : ''}`}>{node.name}</span>
        {isSupported && (
          <CheckCircle className="h-3 w-3 text-green-600 flex-shrink-0" />
        )}
        <span className="text-xs text-gray-500 flex-shrink-0 w-16 text-right">{formatFileSize(node.size || 0)}</span>
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleHidePath(node.path, node.name);
          }}
          className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-gray-200 rounded flex-shrink-0 w-6 h-6 flex items-center justify-center"
          title={`${description} - ${isHidden ? "Show" : "Hide"} file`}
        >
          {isHidden ? <Eye className="h-3 w-3 text-gray-500" /> : <EyeOff className="h-3 w-3 text-gray-500" />}
        </button>
      </div>
    );
  }
  
  return (
    <div>
      <div 
        className="flex items-center gap-2 py-1 text-sm hover:bg-gray-50 cursor-pointer group min-h-[28px]"
        style={{ paddingLeft: `${level * 20 + 4}px` }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleToggleExpand();
          }}
          className="flex items-center gap-2 flex-1 min-w-0"
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
          )}
          <Folder className={`h-4 w-4 flex-shrink-0 ${isHidden ? 'text-gray-400' : 'text-blue-500'}`} />
          <span className={`text-gray-700 font-medium truncate ${isHidden ? 'opacity-50 line-through' : ''}`}>{node.name}</span>
        </button>
        {node.children && (
          <span className="text-xs text-gray-500 flex-shrink-0">
            {node.children.length} {node.children.length === 1 ? 'item' : 'items'}
          </span>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation();
            toggleHidePath(node.path || '', node.name);
          }}
          className="opacity-0 group-hover:opacity-100 ml-2 p-1 hover:bg-gray-200 rounded flex-shrink-0 w-6 h-6 flex items-center justify-center"
          title={isHidden ? "Show folder" : "Hide folder"}
        >
          {isHidden ? <Eye className="h-4 w-4 text-gray-500" /> : <EyeOff className="h-4 w-4 text-gray-500" />}
        </button>
      </div>
      {expanded && node.children && !isHidden && (
        <div>
          {node.children
            .filter((child: any) => {
              if (child.type === 'file') {
                const ext = child.extension || '';
                return selectedExtensions.length === 0 || selectedExtensions.includes(ext);
              }
              return true;
            })
            .map((child: any, idx: number) => (
              <FileTreeNode 
                key={idx} 
                node={child} 
                level={level + 1}
                toggleHidePath={toggleHidePath}
                hiddenPaths={hiddenPaths}
                formatFileSize={formatFileSize}
                supportedExtensions={supportedExtensions}
                extensionDescriptions={extensionDescriptions}
                selectedExtensions={selectedExtensions}
                expandedTreePaths={expandedTreePaths}
                setExpandedTreePaths={setExpandedTreePaths}
              />
            ))}
        </div>
      )}
    </div>
  );
});

FileTreeNode.displayName = 'FileTreeNode';

